import { AuthProvider, UserIdentity } from "react-admin";

// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import {
    getAuth,
    setPersistence,
    browserLocalPersistence,
    signInWithPopup,
    GoogleAuthProvider,
    Auth,
    OAuthProvider,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signInWithCustomToken,
} from "firebase/auth";
import { setSentryUser } from "./sentry";

const LOGIN_EXPIRE_SECONDS = 60 * 60 * 24 * 5;

var firebaseConfig;
if (import.meta.env.MODE === "production") {
    firebaseConfig = {
        apiKey: "AIzaSyAwMv20MvpiyQFSZz3RsbG3X6hSd30uxnA",
        authDomain: "wave-prod-dae70.firebaseapp.com",
        projectId: "wave-prod-dae70",
        storageBucket: "wave-prod-dae70.appspot.com",
        messagingSenderId: "719372490486",
        appId: "1:719372490486:web:885f1ef82ce25abdf33ed7",
        measurementId: "G-4E3ZD9R8CT",
    };
} else if (import.meta.env.MODE === "staging-legacy" || import.meta.env.MODE === "staging") {
    firebaseConfig = {
        apiKey: "AIzaSyAjSoEOw2QDvGnbmTvpkifCLwUBtWN5heU",
        authDomain: "wave-staging-baedb.firebaseapp.com",
        projectId: "wave-staging-baedb",
        storageBucket: "wave-staging-baedb.appspot.com",
        messagingSenderId: "899736006792",
        appId: "1:899736006792:web:0f085237a625730b706f3d",
        measurementId: "G-95D5F67HCL",
    };
} else if (import.meta.env.MODE === "development") {
    firebaseConfig = {
        apiKey: "AIzaSyAkbvLxQHjACHNIfw3X0f5C6PmJQsTv4Yo",
        authDomain: "wave-dev-21daf.firebaseapp.com",
        projectId: "wave-dev-21daf",
        storageBucket: "wave-dev-21daf.appspot.com",
        messagingSenderId: "242157271691",
        appId: "1:242157271691:web:255def0be416d4b4a1b28e",
    };
} else {
    throw new Error("Missing firebase config");
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export type WavePermissions = {
    mode: "admin";
};

export const getFirebaseAuth = async (): Promise<Auth> => {
    const auth = getAuth();
    await setPersistence(auth, browserLocalPersistence);
    return auth;
};

export const authProvider: AuthProvider = {
    login: async ({ username, password }) => {
        throw Error("Unsupported");
    },
    sendEmailLink: async (email: string) => {
        const auth = await getFirebaseAuth();
        const actionCodeSettings = {
            url: window.location.origin + "/#/auth-callback",
            handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        window.localStorage.setItem("emailForSignIn", email);
    },
    handleCallback: async () => {
        const auth = await getFirebaseAuth();

        if (isSignInWithEmailLink(auth, window.location.href)) {
            let email = window.localStorage.getItem("emailForSignIn");

            if (!email) {
                email = window.prompt("Please provide your email for confirmation");
            }

            if (!email) {
                throw Error("Missing email address");
            }

            const result = await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem("emailForSignIn");

            let token = await result.user.getIdToken();
            await authProvider.registerUser(auth, token);
            return true;
        }
        return false;
    },
    loginWithGoogle: async () => {
        const auth = await getFirebaseAuth();
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        let token = await result.user.getIdToken();

        await authProvider.registerUser(auth, token);
    },
    loginWithApple: async () => {
        const auth = await getFirebaseAuth();
        const provider = new OAuthProvider("apple.com");
        const result = await signInWithPopup(auth, provider);
        let token = await result.user.getIdToken();

        await authProvider.registerUser(auth, token);
    },
    registerUser: async (auth: Auth, token: string): Promise<void> => {
        const request = new Request(import.meta.env.VITE_SERVER_URL + "/cms/register", {
            method: "POST",
            headers: new Headers({ Authorization: "Bearer " + token }),
        });
        let response = await fetch(request);
        if (response.status === 401) {
            throw new Error("Unauthorized");
        }
        if (!response.ok) {
            throw new Error("Request failed");
        }
        let data = await response.json();
        let customToken = data["custom_token"];

        await signInWithCustomToken(auth, customToken);

        const allowedUserTypes = ["superuser", "cms_reader", "cms_writer", "cms_external"];

        if (!allowedUserTypes.includes(data.permission_type)) {
            throw new Error("Access denied");
        }
    },
    checkError: (error) => {
        if (error) {
            console.log(error);
        }
        const status = error?.status;
        if (status === 401) {
            return Promise.reject();
        }
        return Promise.resolve();
    },
    checkAuth: async () => {
        const auth = await getFirebaseAuth();
        const user = auth.currentUser;

        if (user) {
            let token = await user.getIdTokenResult();
            if (token.claims.cms !== true) {
                setSentryUser(null);
                return Promise.reject();
            }

            if (
                typeof token.claims.user_type === "string" &&
                ["cms_writer", "superuser"].includes(token.claims.user_type)
            ) {
                if (user.metadata.lastSignInTime === undefined) {
                    return Promise.reject();
                }

                let loginTs = new Date(user.metadata.lastSignInTime).getTime();
                var currentTs = new Date().getTime();

                if (currentTs - loginTs > LOGIN_EXPIRE_SECONDS * 1000) {
                    return Promise.reject();
                }
            }

            setSentryUser({ id: user.uid, email: user.email ?? undefined });
            return Promise.resolve();
        } else {
            setSentryUser(null);
            return Promise.reject();
        }
    },
    getPermissions: async () => {
        const auth = await getFirebaseAuth();
        let token = await auth.currentUser?.getIdTokenResult();
        if (!token) {
            return {};
        }
        return token.claims.cms_permission_type;
    },
    logout: async (params) => {
        const auth = await getFirebaseAuth();

        setSentryUser(null);
        await auth.signOut();
    },
    getIdentity: async () => {
        const auth = await getFirebaseAuth();
        const user = auth.currentUser;

        if (user) {
            const identity: UserIdentity = {
                id: user.uid,
                fullName: user.email || undefined,
                avatar: user.photoURL || undefined,
            };
            return Promise.resolve(identity);
        } else {
            return Promise.reject();
        }
    },
    // authorization
};
